import type { OrganisationChanged } from "@/shared/api/types";

/**
 * Tabs share one cookie, so when one tab moves the session to another organisation (a move after access
 * ended, a switch, a new organisation, an accepted invitation) every other tab is now running on it too
 * (ADR-0011 addendum 2026-10-04, plan §5.6). This channel tells them at once; a tab that misses it is
 * caught by `ORG_MISMATCH` on its next request.
 *
 * One channel object per tab, used to post and to listen: a `BroadcastChannel` never hears its own
 * posts, so the tab that moved is not told about itself.
 */
const CHANNEL = "nacs-session";

export interface OrgChangedMessage {
  type: "org-changed";
  /** The organisation the session is in now. */
  tenantId: string;
  /** Present when it was a move, so every tab says the same thing about why. */
  move?: OrganisationChanged;
}

let channel: BroadcastChannel | null | undefined;

function sessionChannel(): BroadcastChannel | null {
  if (channel === undefined) {
    channel = typeof BroadcastChannel === "function" ? new BroadcastChannel(CHANNEL) : null;
    // Node's implementation (the test runner) keeps the process alive while a channel is open.
    (channel as { unref?: () => void } | null)?.unref?.();
  }
  return channel;
}

export function announceOrgChange(tenantId: string, move?: OrganisationChanged): void {
  const message: OrgChangedMessage = { type: "org-changed", tenantId, ...(move ? { move } : {}) };
  sessionChannel()?.postMessage(message);
}

function parse(data: unknown): OrgChangedMessage | null {
  if (typeof data !== "object" || data === null) return null;
  const m = data as Partial<OrgChangedMessage>;
  return m.type === "org-changed" && typeof m.tenantId === "string" ? (m as OrgChangedMessage) : null;
}

/** Calls `handler` for every `org-changed` another tab posts; returns the unsubscribe. */
export function listenForOrgChanges(handler: (message: OrgChangedMessage) => void): () => void {
  const ch = sessionChannel();
  if (!ch) return () => undefined;
  const onMessage = (event: MessageEvent<unknown>) => {
    const message = parse(event.data);
    if (message) handler(message);
  };
  ch.addEventListener("message", onMessage);
  return () => ch.removeEventListener("message", onMessage);
}
